import { Route, Routes, Navigate, Outlet, useParams } from 'react-router-dom';
import { useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { FiAlertTriangle, FiLock, FiShieldOff } from 'react-icons/fi';
import './App.css';
import Dashboard from './pages/dashboard';
import Trade from './pages/trade';
import Buy from './pages/buy';
import BuyMerchants from './pages/buyMerchants';
import Sell from './pages/sell';
import SellMerchants from './pages/sellMerchants';
import Account from './pages/account';
import BankAccounts from './pages/bankAccounts';
import Support from './pages/support';
import BuyReport from './pages/buyReport';
import SellReport from './pages/sellReport';
import BottomNav from './components/BottomNav';
import Header from './components/Header';

import { getTheme, hasToken } from './helpers';

function Unauthorized() {
  return (
    <div className="unauthorized-screen" role="alert" aria-live="assertive">
      <div className="unauthorized-content">
        <div className="unauthorized-icon-wrap" aria-hidden="true">
          <FiShieldOff className="unauthorized-icon" size={56} />
          <span className="unauthorized-icon-badge">
            <FiLock size={16} />
          </span>
        </div>
        <div className="unauthorized-label">
          <FiAlertTriangle size={18} aria-hidden="true" />
          <span>Access denied</span>
        </div>
        <h1 className="unauthorized-title">Unauthorized</h1>
        <p className="unauthorized-message">
          A valid access token is required. Open the link that was shared with you to continue.
        </p>
      </div>
    </div>
  );
}

function AppFrame({ theme, setTheme, showChrome, children }) {
  return (
    <div className={`app-root app-root-${theme}`}>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3500,
          style: {
            background: '#1e293b',
            color: '#f1f5f9',
            borderRadius: '10px',
          },
          success: { iconTheme: { primary: '#22c55e' } },
          error: { iconTheme: { primary: '#fca5a5' } },
        }}
      />
      <div className={`app-shell app-shell-${theme}`}>
        {showChrome && <Header theme={theme} setTheme={setTheme} />}
        <main className="app-shell-content">
          <section className="screen">{children}</section>
        </main>
        {showChrome && <BottomNav />}
      </div>
    </div>
  );
}

function UnauthorizedPage() {
  const [theme, setTheme] = useState(() => getTheme());

  return (
    <AppFrame theme={theme} setTheme={setTheme} showChrome={false}>
      <Unauthorized />
    </AppFrame>
  );
}

function TokenLayout() {
  const { token } = useParams();
  const [theme, setTheme] = useState(() => getTheme());

  if (!hasToken(token)) {
    return <UnauthorizedPage />;
  }

  return (
    <AppFrame theme={theme} setTheme={setTheme} showChrome>
      <Outlet />
    </AppFrame>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<UnauthorizedPage />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/signup" element={<Navigate to="/" replace />} />
      <Route path="/register" element={<Navigate to="/" replace />} />

      <Route path="/:token" element={<TokenLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="trade" element={<Trade />} />
        <Route path="buy" element={<BuyMerchants />} />
        <Route path="buy/pay" element={<Buy />} />
        <Route path="sell" element={<SellMerchants />} />
        <Route path="sell/pay" element={<Sell />} />
        <Route path="dashboard" element={<Navigate to=".." replace />} />
        <Route path="account" element={<Account />} />
        <Route path="bank-accounts" element={<BankAccounts />} />
        <Route path="buy-report" element={<BuyReport />} />
        <Route path="sell-report" element={<SellReport />} />
        <Route path="support" element={<Support />} />
        <Route path="*" element={<Unauthorized />} />
      </Route>

      <Route path="*" element={<UnauthorizedPage />} />
    </Routes>
  );
}

export default App;
